// src/export.js
//
// ADR-033: client-side zip assembly + browser download for admin exports.
// The export-session-data edge function returns the full bundle as JSON;
// this module shapes it into CSV + JSON files inside a .zip and triggers
// a browser download.
//
// CSV columns (ADR-033 §CSV schema, amended by ADR-042)
// -----------------------------------------------------
//   session_code, group_name, topic, email, first_name, opted_in, model,
//   created_at, deleted
//
// - `email` is blank when `opted_in` is false (ADR-014 retention).
// - `first_name` is presentational and is NOT redacted by opt-in
//   (ADR-042 §5). Blank when the attendee left the field empty.
// - `session_code` is included even for single-session exports so the
//   CSV stays meaningful if merged with other exports later.
// - Soft-deleted submissions are included but marked with a trailing
//   `_deleted` column so marketing can filter them out.
//
// Filenames
// ---------
//   hap-export-all-<ISO-date>.zip        (global)
//   hap-export-<CODE>-<ISO-date>.zip     (single-session)

import JSZip from 'jszip'

/** Escape a single CSV cell. Always quoted; doubles embedded quotes. */
function csvCell(value) {
  if (value === null || value === undefined) return '""'
  const s = String(value)
  return `"${s.replace(/"/g, '""')}"`
}

/** Build the submissions CSV string from the bundle. */
function buildSubmissionsCsv(submissions) {
  const headers = [
    'session_code',
    'group_name',
    'topic',
    'email',
    'first_name',
    'opted_in',
    'model',
    'created_at',
    'deleted',
  ]
  const lines = [headers.map(csvCell).join(',')]
  for (const row of submissions) {
    lines.push([
      csvCell(row.session_code),
      csvCell(row.group_name),
      csvCell(row.topic),
      csvCell(row.email || ''),
      csvCell(row.first_name || ''),
      csvCell(row.opted_in_to_newsletter ? 'true' : 'false'),
      csvCell(row.classification_model),
      csvCell(row.created_at),
      csvCell(row.deleted_at ? 'true' : 'false'),
    ].join(','))
  }
  return lines.join('\n') + '\n'
}

/** Build the groups CSV string from the bundle (global export only; */
/* single-session gets this too for consistency). */
function buildGroupsCsv(groups) {
  const headers = [
    'session_code',
    'group_name',
    'name_version',
    'member_count',
    'created_at',
    'archived',
  ]
  const lines = [headers.map(csvCell).join(',')]
  for (const row of groups) {
    lines.push([
      csvCell(row.session_code),
      csvCell(row.name),
      csvCell(row.name_version),
      csvCell(row.member_count),
      csvCell(row.created_at),
      csvCell(row.archived_at ? 'true' : 'false'),
    ].join(','))
  }
  return lines.join('\n') + '\n'
}

/** Build the sessions CSV (metadata table). Handy for picking codes. */
function buildSessionsCsv(sessions) {
  const headers = [
    'code',
    'name',
    'facilitator_name',
    'state',
    'total_submissions',
    'total_groups',
    'opt_in_count',
    'created_at',
    'export_delivered_at',
  ]
  const lines = [headers.map(csvCell).join(',')]
  for (const row of sessions) {
    lines.push([
      csvCell(row.code),
      csvCell(row.name),
      csvCell(row.facilitator_name),
      csvCell(row.state),
      csvCell(row.total_submissions),
      csvCell(row.total_groups),
      csvCell(row.opt_in_count),
      csvCell(row.created_at),
      csvCell(row.export_delivered_at || ''),
    ].join(','))
  }
  return lines.join('\n') + '\n'
}

/** Trigger a browser download for a Blob with a filename. */
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Revoke next tick so Safari actually has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/**
 * Build a zip from the bundle returned by the export-session-data edge
 * function, then trigger a browser download.
 *
 * @param {object} bundle - { meta, sessions, submissions, groups, ok: true }
 * @param {string} [explicitFilename] - overrides auto-generated filename
 */
export async function downloadExportZip(bundle, explicitFilename) {
  const zip = new JSZip()

  const isoDate = new Date().toISOString().slice(0, 10)
  const scope = bundle?.meta?.scope === 'session' ? 'session' : 'all'

  let filename = explicitFilename
  if (!filename) {
    if (scope === 'session') {
      const code = bundle.sessions?.[0]?.code || 'unknown'
      filename = `hap-export-${code}-${isoDate}.zip`
    } else {
      filename = `hap-export-all-${isoDate}.zip`
    }
  }

  zip.file('submissions.csv', buildSubmissionsCsv(bundle.submissions || []))
  zip.file('groups.csv', buildGroupsCsv(bundle.groups || []))
  zip.file('sessions.csv', buildSessionsCsv(bundle.sessions || []))
  zip.file('archive.json', JSON.stringify(bundle, null, 2))

  const blob = await zip.generateAsync({ type: 'blob' })
  downloadBlob(blob, filename)
  return filename
}
